// React Query hooks for user management
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { User, UserSearchResult, UserUpdatePayload } from '@/types/user';
import {
  searchUsers,
  getUsersBySchool,
  getUserById,
  updateUser,
  deleteUser,
  toggleAmbassador,
} from '@/services/users.service';

/**
 * Hook to search users globally
 */
export function useSearchUsers(query: string) {
  return useQuery<UserSearchResult[]>({
    queryKey: ['userSearch', query],
    queryFn: () => searchUsers(query),
    enabled: query.length > 3,
  });
}

/**
 * Hook to get all users for a school
 */
export function useUsersBySchool(schoolId: string | null) {
  return useQuery<User[]>({
    queryKey: ['users', schoolId],
    queryFn: () => getUsersBySchool(schoolId!),
    enabled: !!schoolId,
  });
}

/**
 * Hook to get a single user by ID
 */
export function useUser(userId: number | null) {
  return useQuery<User>({
    queryKey: ['user', userId],
    queryFn: () => getUserById(userId!),
    enabled: !!userId,
  });
}

/**
 * Hook to update a user
 */
export function useUpdateUser(schoolId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: number;
      data: UserUpdatePayload;
    }) => updateUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ['users', schoolId] });
      }
    },
  });
}

/**
 * Hook to delete a user
 */
export function useDeleteUser(schoolId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ['users', schoolId] });
      }
    },
  });
}

/**
 * Hook to toggle ambassador status
 */
export function useToggleAmbassador(schoolId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      toggledOn,
    }: {
      userId: number;
      toggledOn: boolean;
    }) => toggleAmbassador(userId, toggledOn),
    onMutate: async ({ userId, toggledOn }) => {
      // Optimistic update
      if (schoolId) {
        await queryClient.cancelQueries({ queryKey: ['users', schoolId] });
        const previousUsers = queryClient.getQueryData<User[]>(['users', schoolId]);

        queryClient.setQueryData<User[]>(['users', schoolId], (old) =>
          old?.map((user) =>
            user.id === userId ? { ...user, isAmbassador: toggledOn } : user
          )
        );

        return { previousUsers };
      }
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (schoolId && context?.previousUsers) {
        queryClient.setQueryData(['users', schoolId], context.previousUsers);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      if (schoolId) {
        queryClient.invalidateQueries({ queryKey: ['users', schoolId] });
      }
    },
  });
}

